import type { Block } from '@emailforge/schemas';
import { Button } from '@emailforge/ui';
import { nanoid } from 'nanoid';
import { useState } from 'react';

import { Icon } from '@/components/ui/icons';
import {
  insertSnippet,
  selectBlock as selectBlockAction,
} from '@/features/editor/editorSlice';
import {
  type Snippet,
  useCreateSnippetMutation,
  useDeleteSnippetMutation,
  useListSnippetsQuery,
} from '@/features/snippets/snippetsApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

/**
 * Per-org panel of saved block fragments. Click insert to append a clone of
 * the snippet's blocks (with fresh ids) to the current canvas. Save Selection
 * captures the currently selected block as a new single-block snippet.
 */
export function SnippetsPanel() {
  const dispatch = useAppDispatch();
  const { data: snippets = [], isLoading } = useListSnippetsQuery();
  const [createSnippet, { isLoading: isCreating }] = useCreateSnippetMutation();
  const [deleteSnippet] = useDeleteSnippetMutation();
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId);
  const document = useAppSelector((s) => s.editor.document);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const selectedBlock = document.blocks.find((b) => b.id === selectedBlockId);

  const handleInsert = (snippet: Snippet) => {
    const cloned = snippet.blocksJson.map((b) => cloneBlockWithNewIds(b));
    dispatch(insertSnippet({ blocks: cloned }));
    if (cloned[0]) dispatch(selectBlockAction(cloned[0].id));
  };

  const handleSaveSelection = async () => {
    setError(null);
    if (!selectedBlock) {
      setError('Select a block on the canvas first.');
      return;
    }
    if (!name.trim()) {
      setError('Give this snippet a name.');
      return;
    }
    try {
      await createSnippet({ name: name.trim(), blocks: [selectedBlock] }).unwrap();
      setName('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save snippet.');
    }
  };

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          padding: 12,
          background: 'var(--paper-2)',
          border: '1px solid var(--line-soft)',
          borderRadius: 'var(--r-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.08, textTransform: 'uppercase' }}>
          Save selection
        </div>
        <input
          className="mf-input"
          placeholder="Snippet name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: '100%', fontSize: 12 }}
        />
        <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.4 }}>
          {selectedBlock
            ? `Will save the selected ${selectedBlock.type} block.`
            : 'Select a block on the canvas to save it as a snippet.'}
        </div>
        <Button
          variant="flame"
          size="sm"
          disabled={!selectedBlock || !name.trim() || isCreating}
          onClick={handleSaveSelection}
          style={{ justifyContent: 'center' }}
        >
          {isCreating ? 'Saving…' : 'Save snippet'}
        </Button>
        {error && (
          <div style={{ fontSize: 11, color: 'var(--err)' }}>{error}</div>
        )}
      </div>

      <div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: 0.08,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Saved snippets {snippets.length > 0 && `· ${snippets.length}`}
        </div>
        {isLoading && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading…</div>
        )}
        {!isLoading && snippets.length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            No snippets yet. Save a polished header, footer, or CTA from the canvas to reuse it across templates.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {snippets.map((s) => (
            <div
              key={s.id}
              className="mf-card"
              style={{
                padding: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--paper)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.name}
                </div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  {s.blocksJson.length} block{s.blocksJson.length === 1 ? '' : 's'}
                  {s.category ? ` · ${s.category}` : ''}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => handleInsert(s)}
                title="Insert at end of canvas"
                style={{ padding: '4px 8px' }}
              >
                <Icon.plus size={12} /> Insert
              </Button>
              <button
                onClick={() => {
                  if (confirm(`Delete snippet "${s.name}"?`)) deleteSnippet(s.id);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--ink-3)',
                  padding: 4,
                }}
                title="Delete snippet"
              >
                <Icon.trash size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function cloneBlockWithNewIds(block: Block): Block {
  const cloned = JSON.parse(JSON.stringify(block)) as Block;
  cloned.id = nanoid(10);
  if (cloned.type === 'columns' && Array.isArray(cloned.children)) {
    for (const col of cloned.children) {
      if (!Array.isArray(col)) continue;
      for (const child of col) {
        if (child && typeof child === 'object' && 'id' in child) {
          (child as { id: string }).id = nanoid(10);
        }
      }
    }
  }
  return cloned;
}
